import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Checkbox, Divider } from 'antd';
import { useMutation } from 'react-query';
import { emailsAPI } from '../../services/api';

const { TextArea } = Input;

const EmailModal = ({ visible, contacts, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedEmails, setSelectedEmails] = useState([]);

  // Initialize selected emails with primary emails when contacts change
  useEffect(() => {
    if (contacts) {
      const primaryEmails = [];
      contacts.forEach(contact => {
        if (contact.emails && contact.emails.length > 0) {
          const primaryEmail = contact.emails.find(email => email.is_primary) || contact.emails[0];
          if (primaryEmail) {
            primaryEmails.push(`${contact.id}-${primaryEmail.id}`);
          }
        }
      });
      setSelectedEmails(primaryEmails);
    }
  }, [contacts]);

  const sendEmailMutation = useMutation(emailsAPI.send, {
    onSuccess: () => {
      message.success('Email envoyé avec succès');
      form.resetFields();
      setSelectedEmails([]);
      onSuccess();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de l\'envoi');
    }
  });

  const handleSubmit = async (values) => {
    if (selectedEmails.length === 0) {
      message.error('Veuillez sélectionner au moins une adresse email');
      return;
    }

    // Extract contact IDs from selected emails (contactId__emailId format)
    // Split on __ to get the full contact UUID
    const contactIds = [...new Set(selectedEmails.map(email => {
      return email.split('__')[0]; // Split on __ instead of -
    }))];
    
    console.log('Selected emails:', selectedEmails);
    console.log('Contact IDs to send:', contactIds);
    
    sendEmailMutation.mutate({
      subject: values.subject,
      content: values.content,
      contact_ids: contactIds
    });
  };

  const handleEmailChange = (checkedValues) => {
    setSelectedEmails(checkedValues);
  };

  const isLoading = sendEmailMutation.isLoading;

  const renderEmailOptions = () => {
    const options = [];
    
    contacts?.forEach((contact, contactIndex) => {
      if (contact.emails && contact.emails.length > 0) {
        // Add contact name as a section header
        options.push(
          <div key={`contact-${contact.id}`} style={{ marginBottom: '12px' }}>
            <strong style={{ fontSize: '14px', color: '#1890ff' }}>{contact.first_name} {contact.last_name}</strong>
          </div>
        );
        
        // Add email options for this contact
        contact.emails.forEach(email => {
          const emailKey = `${contact.id}__${email.id}`; // Use __ as separator to avoid conflicts with UUID dashes
          const isPrimary = email.is_primary;
          
          options.push(
            <div key={emailKey} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', marginLeft: '8px' }}>
              <Checkbox 
                value={emailKey}
                style={{ marginRight: '8px' }}
              />
              <span style={{ 
                fontWeight: isPrimary ? 'bold' : 'normal',
                color: isPrimary ? '#1890ff' : 'inherit'
              }}>
                {email.email_address}
              </span>
              {isPrimary && (
                <span style={{ color: '#1890ff', marginLeft: '8px', fontSize: '12px' }}>(Principal)</span>
              )}
            </div>
          );
        });
        
        // Add divider except for last contact
        if (contactIndex < contacts.length - 1) {
          options.push(<Divider key={`divider-${contact.id}`} style={{ margin: '16px 0' }} />);
        }
      }
    });
    
    return options;
  };

  return (
    <Modal
      title="Envoyer un email"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="subject"
          label="Sujet"
          rules={[{ required: true, message: 'Veuillez saisir le sujet' }]}
        >
          <Input placeholder="Sujet de l'email" />
        </Form.Item>

        <Form.Item label="Destinataires">
          <Checkbox.Group
            value={selectedEmails}
            onChange={handleEmailChange}
            style={{ width: '100%' }}
          >
            {renderEmailOptions()}
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          name="content"
          label="Contenu"
          rules={[{ required: true, message: 'Veuillez saisir le contenu' }]}
        >
          <TextArea 
            placeholder="Contenu de l'email (HTML supporté)" 
            rows={8}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} block>
            Envoyer l'email
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EmailModal;
